import { useMemo } from 'react'

import type { IComputedValue } from 'mobx'
import { computed, isObservable } from 'mobx'

/**
 * createComputed
 * =============
 *
 * This hook allows us to generate computed values in order to memoize
 * expensive operations.
 * It accepts a mix of observable and not observable values and it will
 * succesfully evaluate only when one of them changes.
 *
 * This returns a computed, which means that you will need to call `.get()`
 * to read it.
 *
 *   ```js
 *   const useUsersByName = createComputed((users, name) => (
 *     users.filter({ name }).length
 *   ))
 *
 *   const Users = ({ users, name }) => {
 *     const usersByName = useUsersByName(users, name)
 *
 *     return <h1>{usersByName.get()}</h1>
 *   }
 *   ```
 */

type Fn = (...args: any[]) => any
interface ComputedOptions {
  name?: string
  keepAlive?: boolean
}

const createComputed = <F extends Fn>(
  fn: F,
  options: ComputedOptions = {}
): ((...args: Parameters<F>) => IComputedValue<ReturnType<F>>) => {
  return (...args) =>
    useMemo(
      () => computed(() => fn(...args), options),
      args.filter((a) => !isObservable(a)) // eslint-disable-line react-hooks/exhaustive-deps
    )
}

export default createComputed
